/**
 * Pictures from the manifest, each with a drawn fallback: Pic (any image),
 * Tibbs (the mascot, inline SVG stand-in), Crest and Shield (the logo), and the
 * medallions for players and worlds.
 */
import { useEffect, useId, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { srcSet, useAssets } from '../assets';
import type { Img } from '../assets';
import { PLAYER_COLORS, PLAYER_GLYPHS, worldFor } from '../brand';
import type { Glyph, World } from '../brand';

export function Pic({
  img,
  alt = '',
  className = '',
  fallback = null,
  sizes,
  set,
  eager = false,
  style,
  portrait,
}: {
  img: Img | undefined;
  /** A portrait crop served to portrait screens through <picture>. */
  portrait?: Img | undefined;
  alt?: string;
  className?: string;
  fallback?: ReactNode;
  sizes?: string;
  /** A w-descriptor srcset; otherwise 1x/2x. */
  set?: string | undefined;
  eager?: boolean;
  style?: CSSProperties;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  useEffect(() => setFailed(null), [img?.src]);
  if (!img || failed === img.src) return <>{fallback}</>;
  const el = (
    <img
      className={className}
      src={img.src}
      srcSet={set ?? srcSet(img)}
      {...(sizes ? { sizes } : {})}
      {...(img.width ? { width: img.width } : {})}
      {...(img.height ? { height: img.height } : {})}
      alt={alt}
      draggable={false}
      decoding="async"
      loading={eager ? 'eager' : 'lazy'}
      style={style}
      onError={() => setFailed(img.src)}
    />
  );
  if (!portrait) return el;
  return (
    <picture className={className}>
      <source media="(orientation: portrait)" srcSet={srcSet(portrait) ?? portrait.src} />
      {el}
    </picture>
  );
}

// ---------------------------------------------------------------------------
// Tibbs (inline stand-in until the art lands)
// ---------------------------------------------------------------------------

export type TibbsPose = 'wry' | 'celebrate' | 'retract' | 'point' | 'applaud' | 'smug' | 'fore';

/**
 * Three flat masses and an ink outline: gold dome, jade head, lapis cap.
 * Good enough at 40 to 72 px; the real art replaces it from the manifest.
 */
export function TibbsSvg({ pose = 'wry', size = 64 }: { pose?: TibbsPose; size?: number }) {
  const ink = '#1B1A2E';
  const retract = pose === 'retract';
  const headY = retract ? 30 : 22;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" className="tibbs-svg">
      {/* club */}
      {pose === 'celebrate' && <path d="M46 30 L56 6" stroke="#9A6A3E" strokeWidth="3" strokeLinecap="round" />}
      {pose === 'point' && <path d="M44 34 L60 24" stroke="#9A6A3E" strokeWidth="3" strokeLinecap="round" />}
      {pose === 'wry' && <path d="M48 58 L54 30" stroke="#9A6A3E" strokeWidth="3" strokeLinecap="round" />}
      {/* legs */}
      <ellipse cx="23" cy="57" rx="5" ry="4" fill="#4FAF84" stroke={ink} strokeWidth="2" />
      <ellipse cx="41" cy="57" rx="5" ry="4" fill="#4FAF84" stroke={ink} strokeWidth="2" />
      {/* head */}
      <g transform={`translate(0 ${headY - 22})`}>
        <ellipse cx="32" cy="22" rx="11" ry="10" fill="#4FAF84" stroke={ink} strokeWidth="2" />
        {/* eyes: heavy lids */}
        <circle cx="28" cy="22" r="2.6" fill="#fff" stroke={ink} strokeWidth="1.2" />
        <circle cx="36" cy="22" r="2.6" fill="#fff" stroke={ink} strokeWidth="1.2" />
        {pose === 'smug' ? (
          <path d="M25.5 22h5M33.5 22h5" stroke={ink} strokeWidth="1.8" strokeLinecap="round" />
        ) : (
          <>
            <circle cx="28.5" cy="22.6" r="1.1" fill={ink} />
            <circle cx="36.5" cy="22.6" r="1.1" fill={ink} />
            <path d="M25.4 21h5.2M33.4 21h5.2" stroke={ink} strokeWidth="1.6" strokeLinecap="round" />
          </>
        )}
        <path d={pose === 'celebrate' || pose === 'fore' ? 'M29 27 q3 3 6 0' : 'M29 27.2 q3 1.4 6 -0.6'} fill="none" stroke={ink} strokeWidth="1.6" strokeLinecap="round" />
        {/* cap */}
        <path d="M21 17 q11 -11 22 0 z" fill="#3048A0" stroke={ink} strokeWidth="2" strokeLinejoin="round" />
        <path d="M40 16.5 q5 0 7 2" fill="none" stroke={ink} strokeWidth="2.4" strokeLinecap="round" />
      </g>
      {/* shell dome */}
      <path d="M12 52 q0 -26 20 -26 q20 0 20 26 z" fill="#E0A93A" stroke={ink} strokeWidth="2.2" strokeLinejoin="round" />
      <path d="M24 34 l8 -4 l8 4 l0 8 l-8 4 l-8 -4 z" fill="#B9832A" opacity="0.55" />
      <path d="M12 52 h40" stroke={ink} strokeWidth="2.2" />
      {pose === 'applaud' && <path d="M14 40 l-4 -4 M50 40 l4 -4" stroke={ink} strokeWidth="2" strokeLinecap="round" />}
      {retract && <path d="M50 18 q2 4 0 6 q-2 -2 0 -6" fill="#9fd3f0" stroke={ink} strokeWidth="1" />}
    </svg>
  );
}

/** Tibbs from the manifest if present (coach / celebrate / retract / stickers), else the stand-in. */
export function Tibbs({ pose = 'wry', size = 64, className = '' }: { pose?: TibbsPose; size?: number; className?: string }) {
  const a = useAssets();
  const img =
    pose === 'point'
      ? a.coach
      : pose === 'celebrate'
        ? (a.tibbsCelebrate ?? a.trophyWin)
        : pose === 'retract'
          ? (a.tibbsRetract ?? a.stickers.get('oof'))
          : pose === 'applaud'
            ? a.stickers.get('well-played')
            : pose === 'smug'
              ? a.stickers.get('smug')
              : pose === 'fore'
                ? a.stickers.get('fore')
                : (a.coach ?? a.stickers.get('smug'));
  return (
    <span className={`tibbs ${className}`} style={{ width: size, height: size }} aria-hidden="true">
      <Pic img={img} fallback={<TibbsSvg pose={pose} size={size} />} />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Crest
// ---------------------------------------------------------------------------

/**
 * The crest's shield drawn in code (tools/crest_svg.py, candidate 1A at 100 units wide): a gold
 * bordure, azure field, a gold chevron between three golf balls. Used on small card backs, where
 * the badge with Tibbs would be too fine, and as the fallback before the manifest loads.
 */
const SHIELD_D = 'M3 3H97V47.28C97 84.47 65.04 107.05 50 113.69C34.96 107.05 3 84.47 3 47.28Z';
const SHIELD_BALLS: [number, number, number][] = [
  [26.72, 30.23, 8.35],
  [73.28, 30.23, 8.35],
  [50, 83.82, 8.79],
];
export function ShieldSvg({ size = 40, tone = 'color' }: { size?: number; tone?: 'color' | 'back' }) {
  const ink = tone === 'back' ? '#1E3570' : '#1B1A2E';
  const clip = `shield-clip-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
  return (
    <svg width={size} height={size * 1.17} viewBox="0 0 100 117" aria-hidden="true" className="shield-svg">
      <defs>
        <clipPath id={clip}>
          <path d={SHIELD_D} />
        </clipPath>
      </defs>
      <path d={SHIELD_D} fill="#2B4C9B" />
      <g clipPath={`url(#${clip})`}>
        <path d="M-11.5 100.51L50 39.02L111.5 100.51V116.99L50 55.49L-11.5 116.99Z" fill="#D9A33B" stroke={ink} strokeWidth="1.1" strokeLinejoin="round" />
        <path d={SHIELD_D} fill="none" stroke={ink} strokeWidth="13.2" />
        <path d={SHIELD_D} fill="none" stroke="#D9A33B" strokeWidth="11.4" />
      </g>
      <path d={SHIELD_D} fill="none" stroke={ink} strokeWidth="3" strokeLinejoin="round" />
      {SHIELD_BALLS.map(([x, y, r], i) => (
        <g key={i}>
          <circle cx={x} cy={y} r={r} fill="#F4EBD9" />
          <path d={`M${x + r * 0.97} ${y - r * 0.25}A${r} ${r} 0 0 1 ${x - r * 0.25} ${y + r * 0.97}C${x + r * 0.3} ${y + r * 0.72} ${x + r * 0.72} ${y + r * 0.3} ${x + r * 0.97} ${y - r * 0.25}Z`} fill="#E6D8BC" />
          <circle cx={x} cy={y} r={r} fill="none" stroke={ink} strokeWidth="1.6" />
        </g>
      ))}
    </svg>
  );
}

/** Primary lockup: crest art from the manifest, or a drawn stand-in with the ANCIENT NINE scroll. */
export function Crest({ width = 200, className = '', motto = false }: { width?: number; className?: string; motto?: boolean }) {
  const a = useAssets();
  return (
    <div className={`crest ${className}`} style={{ '--crest-w': `${width}px` } as CSSProperties}>
      <Pic
        img={motto ? (a.crestMotto ?? a.crest) : a.crest}
        alt="Ancient Nine"
        eager
        className="crest-img"
        fallback={
          a.loaded ? (
            <div className="crest-fallback" role="img" aria-label="Ancient Nine">
              <Tibbs pose="wry" size={width * 0.3} className="crest-tibbs" />
              <ShieldSvg size={width * 0.5} />
              <span className="crest-scroll">ANCIENT NINE</span>
            </div>
          ) : (
            <div className="crest-fallback crest-loading" />
          )
        }
      />
    </div>
  );
}

/** Shield-only mark (top bar, card back). */
export function Shield({ size = 28, tone = 'color' }: { size?: number; tone?: 'color' | 'back' }) {
  const a = useAssets();
  const img = tone === 'back' ? (a.badgeLapis ?? a.badge ?? a.shield) : (a.badge ?? a.shield);
  return (
    <span className="shield" style={{ width: size, height: size * 1.12 }} aria-hidden="true">
      <Pic img={img} eager fallback={<ShieldSvg size={size} tone={tone} />} />
    </span>
  );
}

/** Horizontal lockup for the top bar: the art's lockup, or shield + ANCIENT NINE set in Cinzel. */
export function Lockup() {
  const a = useAssets();
  return (
    <span className="topbar-lockup" aria-hidden="true">
      <Pic
        img={a.lockup}
        className="lockup-img"
        eager
        fallback={
          <>
            <Shield size={24} />
            <span className="mark">ANCIENT NINE</span>
          </>
        }
      />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Medallions
// ---------------------------------------------------------------------------

const GLYPH_PATHS: Record<Glyph, ReactNode> = {
  sun: (
    <>
      <circle cx="12" cy="12" r="4" fill="currentColor" />
      <path d="M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21M5.6 5.6l1.8 1.8M16.6 16.6l1.8 1.8M5.6 18.4l1.8-1.8M16.6 7.4l1.8-1.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  wave: <path d="M3 14c2-3 4-3 6 0s4 3 6 0 4-3 6 0M3 9c2-3 4-3 6 0s4 3 6 0 4-3 6 0" stroke="currentColor" strokeWidth="2.2" fill="none" strokeLinecap="round" />,
  leaf: (
    <>
      <path d="M5 19C5 10 10 5 19 5c0 9-5 14-14 14z" fill="currentColor" />
      <path d="M5 19L14 10" stroke="rgba(0,0,0,.35)" strokeWidth="1.6" strokeLinecap="round" />
    </>
  ),
  star: <path d="M12 3.5l2.6 5.4 5.9.8-4.3 4.1 1 5.8L12 16.8l-5.2 2.8 1-5.8-4.3-4.1 5.9-.8z" fill="currentColor" />,
  moon: <path d="M15.5 3.5A8.5 8.5 0 1 0 20.5 15 7 7 0 0 1 15.5 3.5z" fill="currentColor" />,
  mountain: <path d="M2.5 19.5L9 8l3.5 5.5L15 10l6.5 9.5z" fill="currentColor" />,
};

export function PlayerMedallion({ seat, size = 32, active = false, className = '' }: { seat: number; size?: number; active?: boolean; className?: string }) {
  const i = ((seat % 6) + 6) % 6;
  const glyph = PLAYER_GLYPHS[i]!;
  return (
    <span
      className={`pmedal${active ? ' active' : ''} ${className}`}
      style={{ '--ms': `${size}px`, '--mc': PLAYER_COLORS[i] } as CSSProperties}
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" width={size * 0.56} height={size * 0.56}>
        {GLYPH_PATHS[glyph]}
      </svg>
    </span>
  );
}

/** A world's landmark medallion (art from the manifest; fallback: accent disc with the world number). */
export function WorldMedallion({ world, size = 24, className = '' }: { world: World; size?: number; className?: string }) {
  const a = useAssets();
  const img = a.worlds.get(world.n)?.medallion;
  return (
    <span className={`wmedal ${className}`} style={{ '--ms': `${size}px`, '--wc': world.accent } as CSSProperties} aria-hidden="true">
      <Pic img={img} fallback={<span className="wmedal-n">{['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX'][world.n - 1]}</span>} />
    </span>
  );
}

export function WorldMedallionFor({ hole, total, size }: { hole: number; total: number; size?: number }) {
  return <WorldMedallion world={worldFor(hole, total)} {...(size ? { size } : {})} />;
}
